import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '.'
import { Mentor, User } from '../models/User'
import { Job } from '../models/Job'
import axios from 'axios'
import { PURGE } from 'redux-persist'
import { JobCategory1 } from '../models/JobCategory1'
import { LoadingStatus } from '../models/Enums'
import { JobSimple } from '../models/JobSimple'
import { UserGroupDto } from '../models/Dto/UserGroupDto'
import { JobRecommend } from '../models/JobRecommend'
import { axiosGet, axiosPost } from '../components/Common/Axios'
import { Inventory } from '../models/Inventory'
import { Setting } from '../models/Setting'
import { Friend } from '../models/Friend'
import { FriendToUser } from '../models/FriendToUser'

const name = 'main'

export type MainState = {
  jobCategories1: JobCategory1[]
  jobs: JobSimple[]

  // 멘토 로그인시 멘티 리스트
  menteeGroups: UserGroupDto[]
  // 멘티 로그인시 멘토 리스트
  mentors: Mentor[]
  // 친구리스트
  friends: FriendToUser[]
  // 인벤토리
  inventories: Inventory[]

  loadingStatus: number
}

const initialState: MainState = {
  jobCategories1: [],
  jobs: [],
  menteeGroups: [],
  mentors: [],
  friends: [],
  inventories: [],

  loadingStatus: LoadingStatus.NONE,
}

// 전체 Job Category 불러오기. 시작시 한번 불러옴
export const asyncGetJobCategories = createAsyncThunk(
  `${name}/job_category`,
  async (_, thunkAPI) => {
    const data = await axiosGet('/api/job/category')
    return await new Promise<any>((resolve) => resolve(data))
  }
)

// mentor의 경우 mentees: UserGroupDto 를 불러옴
export const asyncGetMentees = createAsyncThunk(
  `${name}/get_mentees`,
  async (id: number, thunkAPI) => {
    const payload = { id }
    const data = await axiosPost('/api/mentor/get_mentees', payload)

    const mentees: UserGroupDto[] = data

    return await new Promise<UserGroupDto[]>((resolve) => resolve(mentees))
  }
)

// mentee의 경우 mentors: Mentor[] 를 불러옴
export const asyncGetMentors = createAsyncThunk(
  `${name}/get_mentors`,
  async (id: number, thunkAPI) => {
    const payload = { id }
    const data = await axiosPost('/api/mentee/get_mentors', payload)
    const mentors: Mentor[] = data

    return await new Promise<Mentor[]>((resolve) => resolve(mentors))
  }
)

// 친구 loading
export const asyncGetFriends = createAsyncThunk(
  `${name}/get_friends`,
  async (id: number, thunkAPI) => {
    const payload = { userId: id }
    const data = await axiosPost('/api/friend/get_friends', payload)
    const friends: FriendToUser[] = data

    return await new Promise<FriendToUser[]>((resolve) => resolve(friends))
  }
)

// inventory loading
export const asyncGetInventories = createAsyncThunk(
  `${name}/get_inventories`,
  async (_, thunkAPI) => {
    const data = await axiosPost('/api/inventory/get_inventories')
    const inventories: Inventory[] = data

    return await new Promise<Inventory[]>((resolve) => resolve(inventories))
  }
)

export const mainSlice = createSlice({
  name,
  initialState,
  reducers: {
    setJobCategories: (state, action: PayloadAction<JobCategory1[]>) => {
      state.jobCategories1 = action.payload
    },

    clearJobCategories: (state) => {
      state.jobCategories1.splice(0, state.jobCategories1.length)
    },

    // inventory리스트에서 inventory를 추가
    insertToInventories: (state, action: PayloadAction<Inventory>) => {
      state.inventories.push(action.payload)
      console.log(state.inventories)
    },

    // inventory리스트에서 inventory를 제거
    removeFromInventories: (
      state,
      action: PayloadAction<{ userId: number; jobId: number }>
    ) => {
      state.inventories = state.inventories.filter(
        (inventory) =>
          inventory.userId !== action.payload.userId ||
          inventory.job.id !== action.payload.jobId
      )
    },

    // Job의 isInInventory를 true로 변경
    setInInventory: (
      state,
      action: PayloadAction<{ jobId: number; userId: number }>
    ) => {
      const job = state.jobs.find((job) => job.id === action.payload.jobId)
      if (!job) {
        return
      }
      job.isInInventory = true
    },
    // Job의 isInInventory를 false로 변경
    setOutInventory: (
      state,
      action: PayloadAction<{ jobId: number; userId: number }>
    ) => {
      const job = state.jobs.find((job) => job.id === action.payload.jobId)
      if (!job) {
        return
      }
      job.isInInventory = false
    },
    updateInventoryReplyCnt: (
      state,
      action: PayloadAction<{ inventoryId: number; replyCnt: number }>
    ) => {
      const inventory = state.inventories.find(
        (inventory) => inventory.id === action.payload.inventoryId
      )
      if (inventory) {
        inventory.replyCnt = action.payload.replyCnt
      }
    },
    updateInventoryMemo: (
      state,
      action: PayloadAction<{ inventoryId: number; memo: string }>
    ) => {
      const inventory = state.inventories.find(
        (inventory) => inventory.id === action.payload.inventoryId
      )
      if (inventory) {
        inventory.memo = action.payload.memo
      }
    },
    updateInventoryPoint: (
      state,
      action: PayloadAction<{ inventoryId: number; point: number }>
    ) => {
      const inventory = state.inventories.find(
        (inventory) => inventory.id === action.payload.inventoryId
      )
      if (inventory) {
        inventory.point = action.payload.point
      }
    },
    updateInventoryLikeCnt: (
      state,
      action: PayloadAction<{ inventoryId: number; likeCnt: number }>
    ) => {
      const inventory = state.inventories.find(
        (inventory) => inventory.id === action.payload.inventoryId
      )
      if (inventory) {
        inventory.likeCnt = action.payload.likeCnt
      }
    },
    updateMenteeInfo: (
      state,
      action: PayloadAction<{
        userGroupId: number
        userId: number
        menteeInfo: string
      }>
    ) => {
      state.menteeGroups = state.menteeGroups.map((group) => {
        const newGroup = { ...group }
        if (group.userGroup.id === action.payload.userGroupId) {
          newGroup.users = group.users.map((user) => {
            if (user.mentee.id === action.payload.userId) {
              return { ...user, menteeInfo: action.payload.menteeInfo }
            } else {
              return user
            }
          })
        }
        return newGroup
      })
    },
    // 멘티에게 Job추천
    // setRecommendMenteeJob: (
    //   state,
    //   action: PayloadAction<{
    //     selectedGroup: number
    //     selectedUser: number
    //     isChecked: boolean
    //   }>
    // ) => {
    //   state.menteeGroups[action.payload.selectedGroup].users[
    //     action.payload.selectedUser
    //   ].checked = action.payload.isChecked
    // },
  },
  extraReducers: (builder) => {
    //----------------------------------------------------------------
    // [getJobCategory 상태]
    //----------------------------------------------------------------
    builder.addCase(asyncGetJobCategories.pending, (state, action) => {
      state.loadingStatus = LoadingStatus.PENDING
    })
    builder.addCase(
      asyncGetJobCategories.fulfilled,
      (state, action: PayloadAction<any>) => {
        const inventories = action.payload['inventories']

        state.jobCategories1 = action.payload['categories']
        state.jobs = []
        state.jobCategories1.map((category1) => {
          category1.categories2.map((category2) => {
            const jobs = category2.jobs.map((job) => {
              job.isInInventory = false
              return job
            })
            state.jobs.push(...jobs)
          })
        })

        inventories.forEach((inventory: Inventory) => {
          const jobs = state.jobs.filter((job) => job.id === inventory.job.id)
          jobs[0].isInInventory = jobs.length > 0
        })

        state.loadingStatus = LoadingStatus.FULFILLED
      }
    )
    builder.addCase(asyncGetJobCategories.rejected, (state, action) => {
      state.jobCategories1 = []
      state.loadingStatus = LoadingStatus.REJECTED
    })

    //----------------------------------------------------------------
    // [asyncGetMentees 상태]
    //----------------------------------------------------------------
    builder.addCase(asyncGetMentees.pending, (state, action) => {
      state.loadingStatus = LoadingStatus.PENDING
    })
    builder.addCase(
      asyncGetMentees.fulfilled,
      (state, action: PayloadAction<UserGroupDto[]>) => {
        state.menteeGroups = action.payload
        state.loadingStatus = LoadingStatus.FULFILLED
      }
    )
    builder.addCase(asyncGetMentees.rejected, (state, action) => {
      state.menteeGroups = []
      state.loadingStatus = LoadingStatus.REJECTED
    })

    //----------------------------------------------------------------
    // [asyncGetMentors 상태]
    //----------------------------------------------------------------
    builder.addCase(asyncGetMentors.pending, (state, action) => {
      state.loadingStatus = LoadingStatus.PENDING
    })
    builder.addCase(
      asyncGetMentors.fulfilled,
      (state, action: PayloadAction<Mentor[]>) => {
        state.mentors = action.payload
        state.loadingStatus = LoadingStatus.FULFILLED
      }
    )
    builder.addCase(asyncGetMentors.rejected, (state, action) => {
      console.log('rejected', action.payload)
      state.mentors = []
      state.loadingStatus = LoadingStatus.REJECTED
    })

    //----------------------------------------------------------------
    // [asyncGetFriends 상태]
    //----------------------------------------------------------------
    builder.addCase(asyncGetFriends.pending, (state, action) => {
      state.loadingStatus = LoadingStatus.PENDING
    })
    builder.addCase(
      asyncGetFriends.fulfilled,
      (state, action: PayloadAction<FriendToUser[]>) => {
        state.friends = action.payload
        state.loadingStatus = LoadingStatus.FULFILLED
      }
    )
    builder.addCase(asyncGetFriends.rejected, (state, action) => {
      state.friends = []
      state.loadingStatus = LoadingStatus.REJECTED
    })

    //----------------------------------------------------------------
    // [asyncGetInventories 상태]
    //----------------------------------------------------------------
    builder.addCase(asyncGetInventories.pending, (state, action) => {
      state.loadingStatus = LoadingStatus.PENDING
    })
    builder.addCase(
      asyncGetInventories.fulfilled,
      (state, action: PayloadAction<Inventory[]>) => {
        state.inventories = action.payload
        state.loadingStatus = LoadingStatus.FULFILLED
      }
    )
    builder.addCase(asyncGetInventories.rejected, (state, action) => {
      state.inventories = []
      state.loadingStatus = LoadingStatus.REJECTED
    })
  },
})

export const {
  updateMenteeInfo,
  insertToInventories,
  removeFromInventories,
  setInInventory,
  setOutInventory,
  updateInventoryReplyCnt,
  updateInventoryPoint,
  updateInventoryMemo,
  updateInventoryLikeCnt,
} = mainSlice.actions

export default mainSlice.reducer
